import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import { FaCamera, FaHeart, FaClock, FaUser, FaEdit, FaInstagram, FaTwitter, FaFacebook, FaImages } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import GlassCard from '../components/GlassCard';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import { normalizeProfileImageUrl } from '../utils/imageHelper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';


const ProfileContainer = styled(motion.div)`
  width: 100%;
  background: linear-gradient(180deg, var(--background-start) 0%, var(--background-end) 100%);
`;

const BannerContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 70vh;
  z-index: 1;
  overflow: hidden;
`;

const Banner = styled(motion.div)`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.bgImage || 'linear-gradient(135deg, var(--gradient-purple) 0%, var(--gradient-pink) 50%, var(--gradient-cyan) 100%)'});
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  padding: 30px;
  padding-bottom: 80px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, transparent 0%, var(--background-start) 90%);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  margin-top: 60vh;
  background: linear-gradient(180deg, transparent 0%, var(--background-start) 5%);
  border-radius: 30px 30px 0 0;
  overflow: hidden;
`;

const UploadButton = styled(motion.label)`
  position: relative;
  z-index: 12;
  background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%);
  backdrop-filter: blur(10px);
  color: white;
  padding: 12px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  border: 1px solid rgba(255,255,255,0.2);
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  
  &:hover {
    background: linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%);
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0,0,0,0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  input {
    display: none;
  }
`;

const ProfileHeader = styled(motion.div)`
  padding: 40px 8% 20px;
  position: relative;
  z-index: 10;
  display: flex;
  align-items: flex-end;
  gap: 40px;
  background: transparent;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;
    padding: 30px 5% 20px;
  }
`;

const AvatarWrapper = styled(motion.div)`
  position: relative;
  width: 180px;
  height: 180px;
  flex-shrink: 0;
  
  label {
    position: absolute;
    bottom: 8px;
    right: 8px;
    width: 44px;
    height: 44px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--gradient-cyan), var(--gradient-purple));
    border: 3px solid var(--background-start);
  }
`;

const Avatar = styled(motion.img)`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid var(--background-start);
  box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1);
  object-fit: cover;
  background: linear-gradient(135deg, var(--gradient-purple), var(--gradient-pink));
`;

const UserInfo = styled(motion.div)`
  padding-bottom: 30px;
  flex: 1;
`;

const UserName = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #fff 0%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 10px;
  text-shadow: 0 4px 8px rgba(0,0,0,0.2);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const UserBio = styled.p`
  color: var(--secondary-color);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 20px;
  max-width: 600px;
`;

const UserStats = styled.div`
  display: flex;
  gap: 30px;
  margin-top: 20px;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 20px;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--secondary-color);
  font-size: 1rem;
  
  svg {
    color: var(--gradient-cyan);
  }
`;

const PlaylistsSection = styled(motion.div)`
  padding: 40px 8% 80px;
  background: var(--background-start);
`;

const FavoritesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 25px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 15px;
  }
`;

const EpisodeSliderContainer = styled.div`
  .swiper-slide {
    width: auto;
  }
`;

const EpisodeCard = styled(Link)`
  display: block;
  width: 280px;
  flex-shrink: 0;
  text-decoration: none;
  
  &:hover .episode-image {
    transform: scale(1.05);
  }
`;

const EpisodeImage = styled.div`
  width: 100%;
  height: 160px;
  border-radius: 12px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  margin-bottom: 12px;
  transition: transform 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%);
  }
`;

const EpisodeInfo = styled.div`
  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: var(--primary-color);
    margin-bottom: 5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  p {
    font-size: 0.85rem;
    color: var(--secondary-color);
  }
`;

const SectionHeader = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
`;

const SectionIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--gradient-purple), var(--gradient-pink));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  box-shadow: 0 8px 25px rgba(0,0,0,0.2);
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const EmptyListMessage = styled(motion.p)`
  color: var(--secondary-color);
  font-style: italic;
  text-align: center;
  padding: 60px 20px;
  font-size: 1.1rem;
  background: rgba(255,255,255,0.05);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
`;

const LoadingContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  font-size: 2rem;
  background: linear-gradient(135deg, var(--background-start), var(--background-end));
`;

const UploadIndicator = styled(motion.div)`
  color: var(--gradient-cyan);
  font-size: 0.9rem;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255,255,255,0.1);
  border-radius: 8px;
  backdrop-filter: blur(10px);
`;

const ProgressBar = styled(motion.div)`
  width: 100px;
  height: 4px;
  background: rgba(255,255,255,0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 5px;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-purple));
  border-radius: 2px;
`;

const EditBioButton = styled.button`
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--secondary-color);
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--gradient-cyan);
    color: var(--gradient-cyan);
  }
`;

const BioTextarea = styled.textarea`
  width: 100%;
  max-width: 600px;
  height: 100px;
  background: rgba(255,255,255,0.1);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: white;
  padding: 12px;
  font-size: 1rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--gradient-cyan);
  }
`;

const SocialMediaLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialIcon = styled.div`
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  font-size: 1.2rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: linear-gradient(135deg, var(--gradient-cyan), var(--gradient-pink));
    color: white;
    transform: translateY(-2px);
    border-color: transparent;
    box-shadow: 0 8px 25px rgba(78, 205, 196, 0.3);
  }
`;

const ContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const ItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const StaggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const ProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null); // Giriş yapan kullanıcı
  const [profileUser, setProfileUser] = useState(null); // Profilde görünen kullanıcı
  const [favorites, setFavorites] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingBio, setEditingBio] = useState(false);
  const [newBio, setNewBio] = useState('');
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Mobil tespiti
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Kullanıcı verilerini çek - TAMAMEN YENİLENDİ
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Önce auth kontrolü (giriş yapmış kullanıcıyı al)
        const authResponse = await fetch(getApiUrl(API_ENDPOINTS.CHECK_AUTH), {
          credentials: 'include'
        });
        
        let loggedInUser = null;
        if (authResponse.ok) {
          const authData = await authResponse.json();
          if (authData.success) {
            loggedInUser = authData.user;
            setUser(authData.user);
          }
        }

        // Profil verilerini çek (URL'deki username'e göre)
        const profileResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_USER_PROFILE)}?username=${username}`, {
          credentials: 'include'
        });
        
        if (!profileResponse.ok) {
          throw new Error('Profil verileri çekilemedi');
        }
        
        const profileData = await profileResponse.json();

        if (profileData.success) {
          setProfileUser(profileData.user);
          setIsOwnProfile(profileData.is_own_profile);
          setNewBio(profileData.user.bio || '');
          
          // Favorileri çek
          await fetchFavorites(username, profileData.is_own_profile);
          
          // Eğer kendi profilimse watchlist'i de çek
          if (profileData.is_own_profile) {
            await fetchWatchlist();
          }
        } else {
          throw new Error(profileData.message);
        }

      } catch (error) {
        console.error('Veri çekme hatası:', error);
        // Eğer kullanıcı bulunamazsa ana sayfaya yönlendir
        if (error.message.includes('Kullanıcı bulunamadı')) {
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate, username]);

  // Favorileri çek - Güncellendi
  const fetchFavorites = async (username, isOwnProfile) => {
    try {
      const endpoint = isOwnProfile 
        ? getApiUrl(API_ENDPOINTS.GET_USER_FAVORITES)
        : `${getApiUrl(API_ENDPOINTS.GET_PUBLIC_FAVORITES)}?username=${username}`;
      
      const favResponse = await fetch(endpoint, {
        credentials: 'include'
      });
      
      if (favResponse.ok) {
        const favData = await favResponse.json();
        if (favData.success && Array.isArray(favData.favorites)) {
          setFavorites(favData.favorites);
        } else {
          setFavorites([]);
        }
      } else {
        setFavorites([]);
      }
    } catch (favError) {
      console.error('Favoriler çekilirken hata:', favError);
      setFavorites([]);
    }
  };

  // Watchlist çek - Sadece kendi profilimizde
  const fetchWatchlist = async () => {
    try {
      const watchResponse = await fetch(getApiUrl(API_ENDPOINTS.GET_USER_WATCHLIST), {
        credentials: 'include'
      });
      
      if (watchResponse.ok) {
        const watchData = await watchResponse.json();
        if (watchData.success) setWatchlist(watchData.watchlist);
      }
    } catch (watchError) {
      console.error('İzleme listesi çekilirken hata:', watchError);
    }
  };

  // Resim yükleme - Sadece kendi profilimizde
  const handleImageUpload = async (e, type) => {
    if (!isOwnProfile || uploading) return;
    
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Sadece resim dosyaları yükleyebilirsiniz');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(getApiUrl(API_ENDPOINTS.UPLOAD_IMAGE), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (data.success) {
        // State'i güncelle
        setProfileUser(prev => ({ 
          ...prev, 
          [type === 'avatar' ? 'avatar_url' : 'banner_url']: data.url 
        }));
        
        setTimeout(() => {
          setUploadProgress(0);
        }, 1000);
      } else {
        alert(data.message);
      }

    } catch (error) {
      console.error('Yükleme hatası:', error);
      alert('Yükleme sırasında hata oluştu');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Bio güncelle - Sadece kendi profilimizde
  const handleBioUpdate = async () => {
    if (!isOwnProfile) return;
    
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.UPDATE_PROFILE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bio: newBio }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setProfileUser(prev => ({ ...prev, bio: newBio }));
        setEditingBio(false);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Bio güncelleme hatası:', error);
      alert('Bio güncellenirken hata oluştu');
    }
  };

  const getImageUrl = (path, size = 'w500') => {
    if (!path || path === 'null' || path === 'undefined' || (typeof path === 'string' && path.trim() === '')) {
      return '/placeholder-image.jpg';
    }
    
    // Eğer path zaten proxy URL ise direkt döndür
    if (path.includes('/proxy-image.php')) return path;
    
    // Eğer path tam bir TMDB URL'si ise, sadece path kısmını çıkar
    if (typeof path === 'string' && path.includes('image.tmdb.org')) {
      const match = path.match(/\/t\/p\/[^\/]+\/(.+)$/);
      if (match) {
        path = '/' + match[1];
      }
    }
    
    // Path'i normalize et - başında / olmalı
    let normalizedPath = typeof path === 'string' ? path.trim() : String(path);
    if (!normalizedPath.startsWith('/') && !normalizedPath.startsWith('http')) {
      normalizedPath = '/' + normalizedPath;
    }
    
    // Proxy URL kullan
    return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(normalizedPath)}&size=${size}`;
  };

  const getMediaTypeLabel = (type) => {
    switch(type) {
      case 'movie': return 'Film';
      case 'tv': return 'Dizi';
      case 'anime': return 'Anime';
      default: return type;
    }
  };

  // Watchlist için bölüm temalı kart oluştur
  const getEpisodeCardData = (item) => {
    // Null/undefined kontrolleri yap
    const tmdbId = item.tmdb_id || item.content_id || 'unknown';
    const seasonNumber = item.season_number || 1;
    const episodeNumber = item.episode_number || 1;
    const mediaType = item.media_type || 'tv';
    
    // Media type'a göre URL oluştur
    let watchUrl;
    if (mediaType === 'movie') {
      watchUrl = `/movie/${tmdbId}`;
    } else {
      watchUrl = `/watch/tv/${tmdbId}/season/${seasonNumber}/episode/${episodeNumber}`;
    }

    return {
      ...item,
      // Episode thumbnail öncelikli, sonra content resimleri
      imageUrl: getImageUrl(item.episode_thumbnail || item.backdrop_path || item.poster_path, 'w500'),
      watchUrl: watchUrl,
      // Content title ana başlık olarak göster (Gachiakuta gibi)
      title: item.title || 'İçerik Bulunamadı',
      season_number: seasonNumber,
      episode_number: episodeNumber
    };
  };

  if (loading) return (
    <LoadingContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        style={{ marginRight: '15px' }}
      >
        ⭐
      </motion.div>
      Yükleniyor...
    </LoadingContainer>
  );

  if (!profileUser) return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Header />
      <div style={{ padding: '100px 5%', textAlign: 'center' }}>
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Kullanıcı bulunamadı.
        </motion.h1>
      </div>
    </motion.div>
  );

  return (
    <ProfileContainer
      variants={ContainerVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <Header />
      
      {/* Sabit Banner */}
      <BannerContainer>
        <Banner 
          bgImage={normalizeProfileImageUrl(profileUser.banner_url) || 'linear-gradient(135deg, var(--gradient-purple) 0%, var(--gradient-pink) 50%, var(--gradient-cyan) 100%)'}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
        </Banner>
      </BannerContainer>

      {/* Kaydırılabilir İçerik */}
      <ContentWrapper>
        {/* Private Profil Kontrolü - Ana Kontrol */}
        {profileUser.profile_visibility === 'private' && !isOwnProfile ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            padding: '100px 20px'
          }}>
            <EmptyListMessage
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              style={{ marginBottom: 0 }}
            >
              🔒 Bu profil gizlidir
            </EmptyListMessage>
          </div>
        ) : (
          <>
            <ProfileHeader
              variants={StaggerContainer}
              initial="hidden"
              animate="visible"
            >
              <AvatarWrapper
                variants={ItemVariants}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Avatar 
                  src={normalizeProfileImageUrl(profileUser.avatar_url) || '/placeholder-image.jpg'} 
                  alt={profileUser.username}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
                  onError={(e) => {
                    e.target.src = '/placeholder-image.jpg';
                  }}
                />
                {isOwnProfile && (
                  <UploadButton 
                    style={{borderRadius: '50%'}}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaCamera />
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleImageUpload(e, 'avatar')}
                      disabled={uploading}
                    />
                  </UploadButton>
                )}
              </AvatarWrapper>
              <UserInfo variants={ItemVariants}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                  <UserName style={{ margin: 0 }}>@{profileUser.username}</UserName>
                  
                  {/* Küçük sosyal medya ikonları username'in yanında */}
                  {(profileUser.instagram_url && profileUser.instagram_url.trim() !== '' ||
                    profileUser.twitter_url && profileUser.twitter_url.trim() !== '' ||
                    profileUser.facebook_url && profileUser.facebook_url.trim() !== '' ||
                    profileUser.tiktok_url && profileUser.tiktok_url.trim() !== '') && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {profileUser.instagram_url && profileUser.instagram_url.trim() !== '' && (
                        <a href={profileUser.instagram_url} target="_blank" rel="noopener noreferrer" 
                           style={{ 
                             width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                             color: '#4ECDC4', transition: 'all 0.3s ease', textDecoration: 'none'
                           }}
                           onMouseOver={(e) => e.target.style.color = '#5FDDD6'}
                           onMouseOut={(e) => e.target.style.color = '#4ECDC4'}
                        >
                          <FaInstagram size={16} />
                        </a>
                      )}
                      {profileUser.twitter_url && profileUser.twitter_url.trim() !== '' && (
                        <a href={profileUser.twitter_url} target="_blank" rel="noopener noreferrer" 
                           style={{ 
                             width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                             color: '#4ECDC4', transition: 'all 0.3s ease', textDecoration: 'none'
                           }}
                           onMouseOver={(e) => e.target.style.color = '#5FDDD6'}
                           onMouseOut={(e) => e.target.style.color = '#4ECDC4'}
                        >
                          <FaTwitter size={16} />
                        </a>
                      )}
                      {profileUser.facebook_url && profileUser.facebook_url.trim() !== '' && (
                        <a href={profileUser.facebook_url} target="_blank" rel="noopener noreferrer" 
                           style={{ 
                             width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                             color: '#4ECDC4', transition: 'all 0.3s ease', textDecoration: 'none'
                           }}
                           onMouseOver={(e) => e.target.style.color = '#5FDDD6'}
                           onMouseOut={(e) => e.target.style.color = '#4ECDC4'}
                        >
                          <FaFacebook size={16} />
                        </a>
                      )}
                      {profileUser.tiktok_url && profileUser.tiktok_url.trim() !== '' && (
                        <a href={profileUser.tiktok_url} target="_blank" rel="noopener noreferrer" 
                           style={{ 
                             width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                             color: '#4ECDC4', transition: 'all 0.3s ease', textDecoration: 'none'
                           }}
                           onMouseOver={(e) => e.target.style.color = '#5FDDD6'}
                           onMouseOut={(e) => e.target.style.color = '#4ECDC4'}
                        >
                          <SiTiktok size={16} />
                        </a>
                      )}
                    </div>
                  )}
                </div>
                
                {isOwnProfile ? (
                  editingBio ? (
                    <div>
                      <BioTextarea
                        value={newBio}
                        onChange={(e) => setNewBio(e.target.value)}
                        placeholder="Kendiniz hakkında bir şeyler yazın..."
                        maxLength={500}
                      />
                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                        <button 
                          onClick={handleBioUpdate}
                          style={{
                            background: 'var(--gradient-cyan)',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          Kaydet
                        </button>
                        <button 
                          onClick={() => {
                            setEditingBio(false);
                            setNewBio(profileUser.bio || '');
                          }}
                          style={{
                            background: 'transparent',
                            color: 'var(--secondary-color)',
                            border: '1px solid var(--border-color)',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <UserBio>
                        {profileUser.bio || 'Henüz bir bio eklenmemiş.'}
                      </UserBio>
                      <EditBioButton onClick={() => setEditingBio(true)}>
                        <FaEdit />
                        {profileUser.bio ? 'Bio\'yu Düzenle' : 'Bio Ekle'}
                      </EditBioButton>
                    </div>
                  )
                ) : (
                  <UserBio>
                    {profileUser.bio || 'Bu kullanıcı henüz bir bio eklememiş.'}
                  </UserBio>
                )}
                
                <UserStats>
                  <StatItem>
                    <FaHeart />
                    <span>{favorites.length} Favori</span>
                  </StatItem>
                  {isOwnProfile && (
                    <StatItem>
                      <FaClock />
                      <span>{watchlist.length} İzleme Listesi</span>
                    </StatItem>
                  )}
                  <StatItem>
                    <FaUser />
                    <span>Üye</span>
                  </StatItem>
                </UserStats>

                <AnimatePresence>
                  {uploading && isOwnProfile && (
                    <UploadIndicator
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      📤 Yükleniyor...
                      <ProgressBar>
                        <ProgressFill
                          initial={{ width: 0 }}
                          animate={{ width: `${uploadProgress}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </ProgressBar>
                    </UploadIndicator>
                  )}
                </AnimatePresence>
              </UserInfo>
            </ProfileHeader>

            <PlaylistsSection
              variants={StaggerContainer}
              initial="hidden"
              animate="visible"
            >
              <SectionHeader variants={ItemVariants}>
                <SectionIcon>
                  <FaHeart />
                </SectionIcon>
                <SectionTitle>Favoriler</SectionTitle>
              </SectionHeader>
          
              <AnimatePresence mode="wait">
                {favorites.length > 0 ?
                  <FavoritesGrid
                    key="favorites-content"
                    as={motion.div}
                    variants={StaggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    {favorites.map(item => (
                      <motion.div key={item.content_id} variants={ItemVariants}>
                        <GlassCard
                          title={item.title}
                          imageUrl={getImageUrl(item.poster_path)}
                          rating={item.vote_average}
                          releaseDate={item.release_date}
                          mediaTypeLabel={getMediaTypeLabel(item.media_type)}
                          isMobile={isMobile}
                          linkTo={`/${item.media_type}/${item.tmdb_id}`}
                        />
                      </motion.div>
                    ))}
                  </FavoritesGrid>
                  :
                  <EmptyListMessage
                    key="favorites-empty"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {isOwnProfile 
                      ? 'Henüz favorilerinize bir şey eklemediniz.'
                      : 'Bu kullanıcı henüz favori eklememiş.'
                    }
                  </EmptyListMessage>
                }
              </AnimatePresence>

              {/* Watchlist sadece kendi profilimizde gösterilsin */}
              {isOwnProfile && (
                <>
                  <SectionHeader variants={ItemVariants} style={{marginTop: '60px'}}>
                    <SectionIcon>
                      <FaClock />
                    </SectionIcon>
                    <SectionTitle>Daha Sonra İzle</SectionTitle>
                  </SectionHeader>
                  
                  <AnimatePresence mode="wait">
                    {watchlist.length > 0 ?
                      <EpisodeSliderContainer
                        key="watchlist-content"
                        as={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: 0.1 }}
                      >
                        <Swiper
                          modules={[Navigation, FreeMode]}
                          spaceBetween={20}
                          slidesPerView={'auto'}
                          freeMode={true}
                          navigation
                        >
                          {watchlist.map(getEpisodeCardData).map((item, index) => (
                            <SwiperSlide key={index}>
                              <EpisodeCard to={item.watchUrl}>
                                <EpisodeImage image={item.imageUrl} className="episode-image" />
                                <EpisodeInfo>
                                  <h3>{item.title}</h3>
                                  <p>{item.season_number}. Sezon {item.episode_number}. Bölüm</p>
                                </EpisodeInfo>
                              </EpisodeCard>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      </EpisodeSliderContainer>
                      :
                      <EmptyListMessage
                        key="watchlist-empty"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        Daha sonra izle listenize bir şey eklemediniz.
                      </EmptyListMessage>
                    }
                  </AnimatePresence>
                </>
              )}
            </PlaylistsSection>
          </>
        )}
      </ContentWrapper>
    </ProfileContainer>
  );
};

export default ProfilePage;
